/**
 * Server-side, `Workflows::WorkflowReviewBatchLiquidJob` zlib-compresses
 * `bodyElement` and `compareBodyElement` and base64-encodes them before
 * publishing on MessageBus. This helper inverts that, returning a new item
 * with raw HTML strings.
 *
 * Items without `bodyCompressed: true` pass through unchanged so an old server
 * paired with a newer client still works during deploys.
 */

type CompressedItem = {
  bodyCompressed: true
  bodyElement: string
  compareBodyElement: string
  [key: string]: unknown
}

type UncompressedItem = {
  bodyCompressed?: false
  bodyElement?: string
  compareBodyElement?: string
  [key: string]: unknown
}

export type MaybeCompressedItem = CompressedItem | UncompressedItem

async function inflateBase64Deflate(input: string): Promise<string> {
  if (!input) return ''

  const compressed = Uint8Array.from(atob(input), (c) => c.charCodeAt(0))
  const ds = new DecompressionStream('deflate')

  const writer = ds.writable.getWriter()
  // Use `Response` as a stream → string adapter: it begins consuming the
  // readable side immediately, so the writer doesn't block on backpressure
  // when we close it below.
  const result = new Response(ds.readable).text()
  await writer.write(compressed)
  await writer.close()
  return result
}

export async function decompressLiquidItem<T extends MaybeCompressedItem>(
  item: T
): Promise<T> {
  if (!item.bodyCompressed) return item

  const [bodyElement, compareBodyElement] = await Promise.all([
    inflateBase64Deflate(item.bodyElement),
    inflateBase64Deflate(item.compareBodyElement),
  ])

  return {
    ...item,
    bodyElement,
    compareBodyElement,
  }
}
